/* eslint-disable global-require */
/* eslint-disable import/no-dynamic-require */
import React, { useEffect, useMemo } from 'react';
import { FaTiktok } from 'react-icons/fa6';
import { MdLocationOn, MdPhone, MdEmail } from 'react-icons/md';
import {
  Layout as AntLayout,
  Grid,
  Col,
  Row,
  message,
  BackTop,
  Button as AntButton,
  Tooltip,
} from 'antd';
import {
  Outlet,
  useLocation,
  Link,
  useOutlet,
  useNavigate,
} from 'react-router-dom';
import styled from 'styled-components';
import {
  FacebookOutlined,
  InfoCircleOutlined,
  TwitterOutlined,
  VerticalAlignTopOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  Button,
  Title,
  Text,
  COLORS,
  FlexCol,
} from 'components';
import ScrollToTop, { getScrollableArea } from 'components/ScrollToTop';
import { menuRoutes } from 'routes';
import {
  ENV_NAME,
  ENV_NAMES_ENUM,
  NAVOJOA,
  REPUVE,
} from 'utils/env';
import { logo, /* asistencia, */ pattern } from 'assets';
import SessionExpiredModal from 'components/SessionExpiredModal';
import { useSelector } from 'react-redux';
import { selectToken } from 'store/auth';

const { Header } = AntLayout;

const socialMediaIcons = {
  facebook: (<FacebookOutlined style={{ fontSize: 22 }} />),
  twitter: (<TwitterOutlined style={{ fontSize: 22 }} />),
  youtube: (<YoutubeOutlined style={{ fontSize: 22 }} />),
  instagram: (<InstagramOutlined style={{ fontSize: 22 }} />),
  tiktok: (<FaTiktok style={{ fontSize: 22 }} />),
};

const states = {
  [ENV_NAMES_ENUM.OAXACA]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://www.facebook.com/municipiooaxacadejuarez/',
      twitter: 'https://x.com/municipiooaxaca',
    },
    footer: 'Municipio de Oaxaca de Juárez 2025-2027',
  },
  [ENV_NAMES_ENUM.NAVOJOA]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://www.facebook.com/NavojoaGob',
      twitter: 'https://x.com/navojoagob',
      instagram: 'https://www.instagram.com/gobiernonavojoa/',
      youtube: 'https://www.youtube.com/channel/UCkVvviLI0SR2cxH28emfF2Q',
    },
    footer: ' 2024 - 2027 | Navojoa al Siguiente Nivel >>>',
  },
  [ENV_NAMES_ENUM.SALAMANCA]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://www.facebook.com/GobSalamanca',
      twitter: 'https://x.com/GobSalamanca',
      youtube: 'https://www.youtube.com/c/presidenciamunicipaldesalamancagto',
      instagram: 'https://www.instagram.com/salamanca_gto_mx/?hl=es',
      tiktok: 'https://www.tiktok.com/@salamanca_gto_mx?is_from_webapp=1&sender_device=pc',
    },
    footer: 'Copyright © 2025 Salamanca Municipio | Funciona con Salamanca Municipio',
  },
  [ENV_NAMES_ENUM.NAYARIT]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://facebook.com/GobNay',
      twitter: !'https://twitter.com/NayaritGobierno',
    },
    footer: '2021-2027 Gobierno Constitucional del Estado de Nayarit',
  },
  [ENV_NAMES_ENUM.JUAREZ]: {
    textColor: 'white',
    socialMedia: {
      facebook: 'https://www.facebook.com/GobiernoCdJuarez',
      twitter: 'https://twitter.com/MunicipioJuarez',
    },
    footer: 'Gobierno Municipal 2021 - 2024 | H. Ayuntamiento de Juárez',
  },
  [ENV_NAMES_ENUM.HERMOSILLO]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://www.facebook.com/hermosillogob',
      twitter: 'https://twitter.com/HermosilloGob',
      youtube: 'https://www.youtube.com/user/HermosilloGob',
      instagram: 'https://www.instagram.com/hermosillogob/',
    },
    footer: 'Portal de Gobierno - H. Ayuntamiento de Hermosillo',
  },
  [ENV_NAMES_ENUM.ROATAN]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://www.facebook.com/AlcaldiadeRoatan',
      twitter: 'https://x.com/alcaldia_roatan?s=21&t=ukmrgy1pNBZDyg73zCBtPw',
      whatsapp: 'https://wa.me/50499018629',
    },
    footer: 'ROATÁN, Municipalidad, Islas de la Bahía, Honduras',
  },
};

const statesRepuve = {
  [ENV_NAMES_ENUM.NAYARIT]: {
    textColor: '',
    socialMedia: {
      facebook: 'https://facebook.com/registropublicovehicular',
    },
    footer: (
      <Link to="https://www.nayarit.gob.mx/" rel="noreferrer" target="_blank">
        2021-2027 - Gobierno Constitucional del Estado de Nayarit
      </Link>
    ),
  },
};

const current = (REPUVE ? statesRepuve : states)[ENV_NAME];

// eslint-disable-next-line react/prop-types
const AppFooter = ({ padding, height, hidePrivacyButton = true }) => {
  const navigation = useNavigate();

  const handlePrivacyClick = useMemo(() => (ENV_NAMES_ENUM.HERMOSILLO === ENV_NAME
    ? () => window.open('https://www.hermosillo.gob.mx/avisosdeprivacidad/', '_blank')
    : () => navigation('/aviso-de-privacidad')), [navigation]);

  return (
    <Footer style={{ padding: `5px ${padding}px`, height }}>
      <Row justify="space-evenly" align="middle">
        <Text style={{ lineHeight: '35px', color: COLORS.white }}>
          {current.footer}
        </Text>

        {!hidePrivacyButton && (
          <Button type="link" onClick={handlePrivacyClick} color={COLORS.white}>
            Aviso de privacidad
          </Button>
        )}

        <div>
          {Object.entries(current.socialMedia).map(([key, url]) => (
            <Button type="link" key={key}>
              <a href={url} target="_blank" rel="noreferrer">
                {socialMediaIcons[key]}
              </a>
            </Button>
          ))}
        </div>
      </Row>
    </Footer>
  );
};

const getHeaderHeight = (screens) => {
  if (ENV_NAME === ENV_NAMES_ENUM.NAYARIT) {
    // return screens.lg || screens.xs ? 'auto' : 'auto';
  }
  return screens.lg ? 100 : 160;
};

const AppLayout = () => {
  const token = useSelector(selectToken);
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const screens = Grid.useBreakpoint();

  const padding = screens.md ? 24 : 12;
  const xPadding = screens.md ? 12 : 3;

  const HEADER_HEIGHT = useMemo(() => getHeaderHeight(screens), [screens]);
  const FOOTER_HEIGHT = screens.md ? 46 : 120;
  const ASSISTANCE_HEIGHT = (screens.md ? 350 : 240) + FOOTER_HEIGHT;

  const selectedKey = useMemo(() => `/${pathname.split('/')[1]}`, [pathname]);
  const outletHasElement = !!outlet?.props?.children?.props?.children;

  const gradients = useMemo(() => ({
    [ENV_NAMES_ENUM.NAYARIT]: `linear-gradient(40deg, #635B5F 0%, ${COLORS.gray} 100%)`,
    [ENV_NAMES_ENUM.JUAREZ]: `linear-gradient(40deg, ${COLORS.gray} 0%, ${COLORS.gray} 100%)`,
    [ENV_NAMES_ENUM.HERMOSILLO]: `linear-gradient(40deg, ${COLORS.white} 0%, ${COLORS.white} 100%)`,
    [ENV_NAMES_ENUM.ROATAN]: 'linear-gradient(40deg, #0084B6 0%, #007474 100%)',
    [ENV_NAMES_ENUM.OAXACA]: 'linear-gradient(40deg, #FFF 0%, #FFF 100%)',
    [ENV_NAMES_ENUM.NAVOJOA]: `linear-gradient(40deg, #635B5F 0%, ${COLORS.gray} 100%)`,
  }), []);

  const borderBottom = ENV_NAME === ENV_NAMES_ENUM.NAVOJOA ? '#C39260' : '#b69d70';

  useEffect(() => {
    message.config({
      top: screens.lg ? 150 : 200,
      duration: 10,
      maxCount: 1,
    });
  }, [screens.lg]);

  const restHeightContent = outletHasElement ? HEADER_HEIGHT + ASSISTANCE_HEIGHT : 0;

  const fullSize = { md: 24 };

  const routes = [
    { regexp: /mi-cuenta/, breakpoints: fullSize },
    { regexp: /tramite\/\d+\/nueva/, breakpoints: fullSize },
    { regexp: /tramites\/seguimiento/, breakpoints: fullSize },
    { regexp: /citas\/nueva|consulta/, breakpoints: fullSize },
    { regexp: /noticias/, breakpoints: fullSize },
  ];

  const defaultBreakpoints = { md: 22, lg: 20, xl: 18 };

  const breakpoints = useMemo(
    () => (selectedKey === '/' ? { md: 24 }
      : routes.find((r) => r.regexp.test(pathname))?.breakpoints) || defaultBreakpoints,
    [selectedKey, pathname, routes],
  );

  const assistanceButtons = useMemo(() => {
    const assistanceLinks = {
      [ENV_NAMES_ENUM.NAYARIT]: 'https://web.whatsapp.com/send?phone=+523113918750',
      [ENV_NAMES_ENUM.ROATAN]: 'https://web.whatsapp.com/send?phone=+50499018629',
      [ENV_NAMES_ENUM.NAVOJOA]: 'https://api.whatsapp.com/send/?phone=5216421571356&text=%C2%A1Bienvenido+Ciudadano%2C+estamos+para+servirte%21&type=phone_number&app_absent=0',
    };

    return assistanceLinks[ENV_NAME] ? (
      <Button color={COLORS.accent} style={{ marginTop: 15 }}>
        <a href={assistanceLinks[ENV_NAME]} rel="noreferrer" target="_blank">
          Asistencia vía WhatsApp
          {' '}
          <WhatsAppOutlined />
        </a>
      </Button>
    ) : null;
  }, []);

  return (
    <React.Fragment key={token}>
      <Layout>
        {/* Header */}
        <Header
          style={{
            padding: screens.md ? `${xPadding}px ${padding}px` : 0,
            backgroundColor: COLORS.gray,
            backgroundImage: gradients[ENV_NAME],
            height: HEADER_HEIGHT,
            borderBottom: `5px solid ${borderBottom}`,
          }}
        >
          <Row
            align="middle"
            justify={screens.lg ? 'space-between' : 'center'}
            style={{
              maxWidth: (ENV_NAME === ENV_NAMES_ENUM.NAYARIT || NAVOJOA) ? 'none' : 1200,
              margin: 'auto',
            }}
          >
            <Col
              lg={6}
              xs={24}
              style={!screens.lg || ENV_NAME === ENV_NAMES_ENUM.NAYARIT
                ? { display: 'flex', justifyContent: 'center' } : {}}
            >
              <Link to="/">
                <Logo />
              </Link>
            </Col>
            <Col xs={24} sm={24} md={24} lg={18}>
              <Row className="menu-routes">
                {menuRoutes.map((r) => (
                  <MenuItem
                    key={r.path}
                    type="link"
                    bg={r.path === selectedKey ? `${COLORS.cream}57` : 'transparent'}
                    $breakLabel={!!r.breakLabel}
                  >
                    <Tooltip title={!screens.xl ? r.label : null} placement="bottom">
                      <Link to={r.path}>
                        <div className="icon" style={{ width: '100% ' }}>
                          {r.icon}
                        </div>
                        {r.label}
                      </Link>
                    </Tooltip>
                  </MenuItem>
                ))}
              </Row>
            </Col>
          </Row>
        </Header>
        <Content
          translatex={screens.md
            ? 50 : 0}
          restheightcontent={screens.md ? HEADER_HEIGHT : null}
          restheightcontentcontent={restHeightContent}
        >
          <div className="site-content" style={{ margin: 0 }}>
            <div className="site-content-content" style={{ margin: 0, padding: screens.md ? `${padding}px 50px 0` : '0' }}>
              <Row justify="center" style={{ minHeight: `calc(100vh - ${restHeightContent + padding * 2}px)` }}>
                <Col xs={24} sm={24} {...breakpoints}>
                  <Outlet />
                </Col>
              </Row>
            </div>

            {/* Assistance */}
            <Row justify="center" align="middle" className="asistencia" style={{ marginTop: screens.md && outletHasElement ? 100 : 0, transform: `translate(0, ${outletHasElement ? 0 : `calc(100vh - ${HEADER_HEIGHT + ASSISTANCE_HEIGHT + padding + 250}px`})`, height: ASSISTANCE_HEIGHT - 100 }}>
              {// screens.md && <Col md={10} lg={8}><SupportImage /></Col>}
              // usar icons, si se proporciona el svg descomentar el anterior y eliminar
              screens.md && (
              <Col md={10} lg={8}>
                <IconContainer>
                  <LocationIconGroup>
                    <MdLocationOn className="main-icon" />
                    <div className="supporting-icons">
                      <MdPhone className="support-icon" />
                      <MdEmail className="support-icon" />
                    </div>
                  </LocationIconGroup>
                </IconContainer>
              </Col>
              )
              // hasta aqui
}
              <Col xs={24} sm={24} md={12} lg={10}>
                <FlexCol alignItems="start" style={{ marginLeft: 'auto', width: 'fit-content', paddingRight: 20 }}>
                  <Title $accent>Ubicación y Contacto</Title>
                  {pathname !== '/asistencia-al-contribuyente' && (
                    <>
                      <Text color={current.textColor} style={{ mixBlendMode: 'luminosity' }}>
                        ¿Conoces nuestra ubicación?
                      </Text>
                      <Button color={COLORS.accent} style={{ marginTop: 15 }}>
                        <Link to="asistencia-al-contribuyente">
                          Encuentranos
                          {' '}
                          <InfoCircleOutlined />
                        </Link>
                      </Button>
                      {assistanceButtons}
                      {ENV_NAME === ENV_NAMES_ENUM.NAYARIT && !REPUVE && (
                        <Button
                          color={COLORS.accent}
                          style={{ marginTop: 15 }}
                        >
                          <a href="https://web.whatsapp.com/send?phone=+523113918750" rel="noreferrer" target="_blank">
                            Asistencia vía WhatsApp
                          </a>
                          <WhatsAppOutlined />
                        </Button>
                      )}
                      {ENV_NAME === ENV_NAMES_ENUM.ROATAN && (
                      <Button
                        color={COLORS.accent}
                        style={{ marginTop: 15 }}
                      >
                        <a href="https://web.whatsapp.com/send?phone=+50499018629" rel="noreferrer" target="_blank">
                          Asistencia vía WhatsApp
                        </a>
                        <WhatsAppOutlined />
                      </Button>
                      )}
                    </>
                  )}
                </FlexCol>
              </Col>
              <AppFooter height={FOOTER_HEIGHT} padding={padding} hidePrivacyButton={pathname === '/aviso-de-privacidad'} />
            </Row>
          </div>

          {!!getScrollableArea(screens) && (
            <BackTop target={() => getScrollableArea(screens)}>
              <Button
                $accent
                style={{
                  width: 40, height: 40, border: '1px solid #f0f2f5', padding: 0,
                }}
              >
                <VerticalAlignTopOutlined style={{ fontSize: 24, margin: 'auto' }} />
              </Button>
            </BackTop>
          )}
        </Content>
      </Layout>
      <ScrollToTop />
      <SessionExpiredModal />
    </React.Fragment>
  );
};

const Layout = styled(AntLayout)`
  height: 100vh;

  @media (max-width: 576px) {
    overflow-y: auto;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
  }

  @media print {
    .ant-layout-header,
    .ant-layout-footer,
    #netpay-checkout,
    .asistencia,
    .ant-btn,
    a {
      display: none !important;
    }
    & {
      height: auto;
      overflow: visible;
    }
  }

  .ant-row {
    max-width: 100vw;
  }

  .ant-spin-nested-loading {
    width: 100%;
    .ant-spin-text, .anticon {
      color: ${COLORS.accent};
    }
    .ant-spin-dot.ant-spin-dot-spin * {
      background-color: ${COLORS.accent};
    }
  }

  .menu-routes {
    @media (max-width: 767px) {
      justify-content: center;
    }
  }

  *, *::before, *::after {
    box-sizing: border-box;
  }
`;

const colorsMenuItemsByEntity = {
  [ENV_NAMES_ENUM.HERMOSILLO]: {
    color: COLORS.cream2,
    hoverColor: COLORS.accent,
  },
  [ENV_NAMES_ENUM.OAXACA]: {
    color: COLORS.accent,
    hoverColor: '#b69d70',
  },
  [ENV_NAMES_ENUM.NAVOJOA]: {
    color: COLORS.white,
    hoverColor: '#b69d70',
  },
};

const currentEntity = colorsMenuItemsByEntity[ENV_NAME] || {
  color: COLORS.white,
  hoverColor: COLORS.white,
};

const MenuItem = styled(AntButton)`
  border: none;
  height: 70px;
  padding: 5px 8px;
  border-radius: 8px;
  color: ${currentEntity.color};
  background: ${(props) => props.bg};
  &:last-child {
    margin-left: ${(props) => (props.$lastToRight ? 'auto' : null)};
  }
  &:hover {
    background: #ffffff19;
  }
  &:active, &:hover, &:visited, &:focus {
    border: none;
    color: ${currentEntity.hoverColor};
  }
  &:active, &:visited, &:focus {
    background: #ffffff19;
  }
  div:first-child {
    width: 100%;
  }
  div:last-child {
    font-size: 16px;
    font-weight: 500;
    ${(props) => props.$breakLabel && `
      width: min-content;
      white-space: break-spaces;
      line-height: 20px;
    `};
  }
  .icon .anticon {
    font-size: 24px;
  }
`;

const asistenciaGradients = {
  [ENV_NAMES_ENUM.NAYARIT]: `linear-gradient(40deg, #f3dbc4 0%, ${COLORS.cream2} 100%)`,
  [ENV_NAMES_ENUM.JUAREZ]: `linear-gradient(40deg, ${COLORS.cream} 0%, ${COLORS.cream2} 100%)`,
  [ENV_NAMES_ENUM.HERMOSILLO]: `linear-gradient(40deg, ${COLORS.white} 0%, ${COLORS.white} 100%)`,
  [ENV_NAMES_ENUM.ROATAN]: 'linear-gradient(40deg, #0083b2 0%, #007474 100%)',
};

const bgPosition = ENV_NAME === ENV_NAMES_ENUM.ROATAN ? 'center' : 'center top';

const Content = styled(AntLayout.Content)`
  overflow: hidden scroll;
  background: #f0f2f5;
  &, .site-content {
    ${(props) => props.restheightcontent && `height: calc(100vh - ${props.restheightcontent}px)`}
  }
  .site-content-content {
    ${(props) => props.restheightcontentcontent && `min-height: calc(100vh - ${props.restheightcontentcontent}px)`}
  }
  .card-container:not(:last-of-type):not(.ant-col-md-12) {
    margin-bottom: 50px;
  }
  .home {
    margin-bottom: 0;
    width: calc(100vw - 5px);
    height: ${REPUVE ? '55' : '81'}vh;
    background-image: url(${pattern});
    background-position: ${bgPosition};
    background-repeat: no-repeat;
    background-size: cover;
    transform: translateX(-${(props) => props.translatex}px) translateY(-24px);
    padding: 30px 0;
    @media only screen and (max-width: 576px) {
      height: auto;
      background-position: center;
    }
  }
  .asistencia {
    background-color: ${COLORS.cream};
    margin-bottom: 0;
    width: 100vw;
    background-image: ${asistenciaGradients[ENV_NAME]};
    a {
      color: ${COLORS.text};
      font-weight: 500;
    }
    .anticon {
      font-size: 18px;
    }
    * {
      margin-bottom: 0 !important;
    }
  }
  @media only screen and (max-width: 576px) {
    overflow: hidden !important;
    position: absolute;
    top: 160px;
    .card-container:not(:last-of-type) {
      margin-bottom: 30px;
    }
    .card-container:last-of-type {
      margin-bottom: 50px;
    }
    .asistencia {
      padding-top: 20px;
    }
    .ant-layout-footer {
      margin-top: 20px
    }
  }
`;

const Footer = styled(AntLayout.Footer)`
  width: 100vw;
  text-align: center;
  padding: 0;
  font-weight: 400;
  background-color: ${COLORS.accent};
  color: ${COLORS.white};
  margin: 0;
  a {
    font-size: 14px;
    color: ${COLORS.white} !important;
    font-weight: 500;
  }
  .anticon {
    font-size: 18px;
  }
`;

const Logo = styled.img.attrs(({ src: logo, alt: 'logo' }))`
  width: 80px;
  @media only screen and (max-width: 992px) {
    margin-left: auto;
    margin-right: auto;
  }
`;
// para los icons
const IconContainer = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   height: 100%;
   padding: 20px;
 `;

const LocationIconGroup = styled.div`
   position: relative;
   display: flex;
   justify-content: center;
   align-items: center;
   
   .main-icon {
     font-size: 120px;
     color: ${COLORS.accent};
     z-index: 2;
   }
   
   .supporting-icons {
     position: absolute;
     bottom: -10px;
     right: -10px;
     display: flex;
     gap: 8px;
     
     .support-icon {
       font-size: 24px;
       color: ${COLORS.primary};
       background: ${COLORS.white};
       border-radius: 50%;
       padding: 4px;
       box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
     }
   }
   
   @media (max-width: 768px) {
     .main-icon {
       font-size: 80px;
     }
     
     .supporting-icons .support-icon {
       font-size: 18px;
     }
   }
 `;
// termina los icons
/* asistencia
const SupportImage = styled.img.attrs(({ src: asistencia, alt: 'asistencia' }))`
  float: right;
  height: 250px;
  transform: translateY(-80px);
`; */

export default AppLayout;
